import * as amplitude from "@amplitude/analytics-browser";
import axios from "axios";
import { Store } from "undux";
import { captureException } from "./sentry";
import { StoreType } from "./stateStore";

export function isProd() {
  return process.env.NODE_ENV === "production";
}

export function isDev() {
  return process.env.NODE_ENV === "development";
}

export function isPayoneerUrl() {
  // very simple way to determine whether this is payoneer's reverse proxy. assuming the url has payoneer in the title
  return window.location.href.includes("payoneer");
}

export function isAirwallexUrl() {
  // Airwallex runs an isolated Link deployment (rutter-link-airwallex.onrender.com).
  return window.location.hostname.includes("airwallex");
}

export function getBackendUrl() {
  const productionUrl =
    process.env.REACT_APP_RUTTER_BACKEND_URL ||
    "https://production.rutterapi.com";
  if (isPayoneerUrl()) {
    // Payoneer has a reverse proxy for our endpoints so we need to return the URL they give us
    // To ensure all backend requests to go their reverse proxy and not ours
    // This is so they can avoid being blocked in China
    return "https://rutter-production.payoneer.com";
  }
  return isProd()
    ? productionUrl
    : process.env.REACT_APP_TUNNEL_ON
    ? "https://rutter.ngrok.io"
    : "http://localhost:4000";
}

export function getBackendWsUrl() {
  if (isProd()) {
    return "wss://d34w0vr6ge7c9f.cloudfront.net";
  } else {
    return "ws://localhost";
  }
}

export enum STORES {
  SHOPIFY = "SHOPIFY",
  MAGENTO = "MAGENTO",
  SHOPLAZZA = "SHOPLAZZA",
  BIG_COMMERCE = "BIG_COMMERCE",
  WOO_COMMERCE = "WOO_COMMERCE",
  SQUARESPACE = "SQUARESPACE",
  WIX = "WIX",
  COMMERCELAYER = "COMMERCELAYER",
  SQUARE = "SQUARE",
  AMAZON = "AMAZON",
  EBAY = "EBAY",
  ETSY = "ETSY",
  FNAC = "FNAC",
  PRESTASHOP = "PRESTASHOP",
  SHOPEE = "SHOPEE",
  SHOPER = "SHOPER",
  WEBFLOW = "WEBFLOW",
  SHOPWARE = "SHOPWARE",
  LAZADA = "LAZADA",
  WALMART = "WALMART",
  MERCADOLIBRE = "MERCADOLIBRE",
  STRIPE = "STRIPE",
  PAYPAL = "PAYPAL",
  ZID = "ZID",
  // accounting
  FRESHBOOKS = "FRESHBOOKS",
  QUICKBOOKS = "QUICKBOOKS",
  QUICKBOOKS_DESKTOP = "QUICKBOOKS_DESKTOP",
  XERO = "XERO",
  NETSUITE = "NETSUITE",
  ZOHOBOOKS = "ZOHOBOOKS",
  SAGE_BUSINESS_CLOUD = "SAGE_BUSINESS_CLOUD",
  SAGE_INTACCT = "SAGE_INTACCT",
  SAGE_INTACCT_V2 = "SAGE_INTACCT_V2",
  SAGE_50 = "SAGE_50",
  SHOPLINE = "SHOPLINE",
  WAVE = "WAVE",
  ALIBABA = "ALIBABA",
  ANFIX = "ANFIX",
  DYNAMICS365 = "DYNAMICS365",
  DYNAMICS365_EMBEDDED = "DYNAMICS365_EMBEDDED",
  SAPBUSINESSONE = "SAPBUSINESSONE",
  SAPS4HANA = "SAPS4HANA",
  TALLY = "TALLY",
  VISTA = "VISTA",
  // Payment processor / Subscription billing services
  RECURLY = "RECURLY",
  CHARGEBEE = "CHARGEBEE",
  CHARGIFY = "CHARGIFY",
  GUMROAD = "GUMROAD",
  // HRIS
  QUICKBOOKS_HRIS = "QUICKBOOKS_HRIS",
  BAMBOO_HR = "BAMBOO_HR",
}

export enum ENV_TYPE {
  Sandbox = "SANDBOX",
  Prod = "PROD",
}

export function extractLinkParamsFromState(state: Store<StoreType>) {
  return {
    link_update_id: state.get("itemId") ?? undefined,
    org_id: state.get("orgId") ?? undefined,
    nonce: state.get("nonce") ?? undefined,
    origin: state.get("origin") ?? undefined,
    custom_redirect_url: state.get("customRedirectUrl") ?? undefined,
  };
}

/**
 * Guards a customer-supplied redirect/return URL before it is handed to a
 * navigation sink (window.location.replace). These arrive as query params on
 * the public connection link, so they are attacker-controlled.
 *
 * The DOM-XSS vector (FND-3230) is a *script-executing scheme* such as
 * `javascript:` (also `data:`, `vbscript:`, `blob:`). Those all parse as
 * absolute URLs with a non-http protocol, so we reject any absolute URL whose
 * scheme is not http(s).
 *
 * We deliberately do NOT require an absolute URL. A scheme-less / relative
 * value (`myapp.com/cb`, `//host`, `/path`) cannot carry a script-executing
 * scheme, and the existing redirect plumbing already supports it (the frontend
 * `buildCustomRedirectUrl` prepends `https://` to scheme-less values; the
 * back-button sink navigates relatively). Rejecting those would be a
 * behavioral regression for any customer relying on that — including the
 * isolated Airwallex deployment, whose Link traffic we can't observe from the
 * main telemetry. So we allow anything that isn't an unsafe absolute scheme.
 */
export function isSafeRedirectUrl(candidate: unknown): candidate is string {
  if (typeof candidate !== "string" || candidate.trim().length === 0) {
    return false;
  }
  let protocol: string;
  try {
    protocol = new URL(candidate).protocol;
  } catch {
    // Not an absolute URL: relative or scheme-less. Cannot execute script via
    // a navigation sink, and is a supported redirect shape — allow it.
    return true;
  }
  // Absolute URL: only http(s) is safe. Blocks javascript:/data:/vbscript:/blob:.
  return protocol === "https:" || protocol === "http:";
}

/**
 * Guards the bank feeds fake login page's `redirect_uri` against open
 * redirects. The backend only ever sends our own Link redirect page here
 * (`getBFOAuthRedirectUri` -> link.rutterapi.com), so on the Airwallex
 * deployment we require that origin (or this page's own). Elsewhere we keep
 * the scheme-only check so other clients' flows are unchanged.
 */
export function isAllowedBankFeedsRedirect(candidate: unknown): boolean {
  if (!isSafeRedirectUrl(candidate)) {
    return false;
  }
  if (!isAirwallexUrl()) {
    return true;
  }
  try {
    const { origin } = new URL(candidate, window.location.origin);
    return (
      origin === window.location.origin ||
      origin === "https://link.rutterapi.com"
    );
  } catch {
    return false;
  }
}

export const getBackButtonCallback = (
  autoPlatform: StoreType["autoPlatform"],
  goBack: () => void,
  customRedirectUrl: string,
) => {
  if (autoPlatform && isSafeRedirectUrl(customRedirectUrl)) {
    return window.location.replace(customRedirectUrl);
  }
  goBack();
};

export const handleClose = () => {
  try {
    window.close();
  } catch (e) {
    captureException(e);
  }
};

export const showBackButton = (
  autoPlatform: StoreType["autoPlatform"],
  customReturnUrl: string,
) => {
  return autoPlatform ? (customReturnUrl ? true : false) : true;
};

const PLATFORM_DETAILS_BY_NAME: Record<
  string,
  { store: STORES; path: string }
> = {
  alibaba: { store: STORES.ALIBABA, path: "/alibaba" },
  amazon: { store: STORES.AMAZON, path: "/amazon" },
  anfix: { store: STORES.ANFIX, path: "/anfix" },
  bamboohr: { store: STORES.BAMBOO_HR, path: "/bamboo_hr" },
  bigcommerce: { store: STORES.BIG_COMMERCE, path: "/bigcommerce" },
  big_commerce: { store: STORES.BIG_COMMERCE, path: "/bigcommerce" },
  chargebee: { store: STORES.CHARGEBEE, path: "/chargebee" },
  dynamics365: { store: STORES.DYNAMICS365, path: "/dynamics365" },
  dynamics365_embedded: {
    store: STORES.DYNAMICS365_EMBEDDED,
    path: "/dynamics365_embedded",
  },
  ebay: { store: STORES.EBAY, path: "/ebay" },
  etsy: { store: STORES.ETSY, path: "/etsy" },
  fnac: { store: STORES.FNAC, path: "/fnac" },
  gumroad: { store: STORES.GUMROAD, path: "/gumroad" },
  freshbooks: { store: STORES.FRESHBOOKS, path: "/freshbooks" },
  lazada: { store: STORES.LAZADA, path: "/lazada" },
  magento: { store: STORES.MAGENTO, path: "/magento" },
  mercadolibre: { store: STORES.MERCADOLIBRE, path: "/mercadoLibre" },
  netsuite: { store: STORES.NETSUITE, path: "/netsuite" },
  paypal: { store: STORES.PAYPAL, path: "/paypal" },
  prestashop: { store: STORES.PRESTASHOP, path: "/prestashop" },
  quickbooks: { store: STORES.QUICKBOOKS, path: "/quickbooks" },
  quickbooks_desktop: {
    store: STORES.QUICKBOOKS_DESKTOP,
    path: "/quickbooks_desktop",
  },
  // Required because platform enum has an underscore
  quickbookshris: { store: STORES.QUICKBOOKS_HRIS, path: "/quickbooks_hris" },
  quickbooks_hris: { store: STORES.QUICKBOOKS_HRIS, path: "/quickbooks_hris" },
  recurly: { store: STORES.RECURLY, path: "/recurly" },
  sagebusinesscloud: {
    store: STORES.SAGE_BUSINESS_CLOUD,
    path: "/sagebusinesscloud",
  },
  sage_business_cloud: {
    store: STORES.SAGE_BUSINESS_CLOUD,
    path: "/sagebusinesscloud",
  },
  sageintacct: { store: STORES.SAGE_INTACCT, path: "/sageintacct" },
  sage_intacct: { store: STORES.SAGE_INTACCT, path: "/sageintacct" },
  sageintacctv2: { store: STORES.SAGE_INTACCT_V2, path: "/sage_intacct_v2" },
  sage_intacct_v2: { store: STORES.SAGE_INTACCT_V2, path: "/sage_intacct_v2" },
  // Required because the enum is SAGE_50 (underscore) but the Route is /sage50.
  // /connections/:id sets autoPlatform from item.platformName ("SAGE_50"), and
  // the fallback path would be /sage_50, which matches nothing.
  sage50: { store: STORES.SAGE_50, path: "/sage50" },
  sage_50: { store: STORES.SAGE_50, path: "/sage50" },
  "sage 50": { store: STORES.SAGE_50, path: "/sage50" },
  // Hosted Sage 50 uses the canonical platform with a backup-upload UI.
  sage50_hosted: { store: STORES.SAGE_50, path: "/sage50" },
  // SAP platforms — DB `name` values contain spaces/slashes that the default
  // `/${name.toLowerCase()}` path can't handle. Map every form (DB name, slug,
  // platform enum) onto the generic-auth route the backend registers.
  "sap business one": {
    store: STORES.SAPBUSINESSONE,
    path: "/sapbusinessone",
  },
  sapbusinessone: { store: STORES.SAPBUSINESSONE, path: "/sapbusinessone" },
  "sap s/4hana": { store: STORES.SAPS4HANA, path: "/saps4hana" },
  saps4hana: { store: STORES.SAPS4HANA, path: "/saps4hana" },
  sap4hana: { store: STORES.SAPS4HANA, path: "/saps4hana" },
  shopee: { store: STORES.SHOPEE, path: "/shopee" },
  shoper: { store: STORES.SHOPER, path: "/shoper" },
  shopify: { store: STORES.SHOPIFY, path: "/shopify" },
  shoplazza: { store: STORES.SHOPLAZZA, path: "/shoplazza" },
  shopware: { store: STORES.SHOPWARE, path: "/shopware" },
  square: { store: STORES.SQUARE, path: "/square" },
  squarespace: { store: STORES.SQUARESPACE, path: "/squarespace" },
  stripe: { store: STORES.STRIPE, path: "/stripe" },
  // Viewpoint Vista: dedicated multi-day onboarding screen, not GenericRoute.
  vista: { store: STORES.VISTA, path: "/vista" },
  "viewpoint vista": { store: STORES.VISTA, path: "/vista" },
  wave: { store: STORES.WAVE, path: "/wave" },
  webflow: { store: STORES.WEBFLOW, path: "/webflow" },
  wix: { store: STORES.WIX, path: "/wix" },
  woocommerce: { store: STORES.WOO_COMMERCE, path: "/woocommerce" },
  woo_commerce: { store: STORES.WOO_COMMERCE, path: "/woocommerce" },
  xero: { store: STORES.XERO, path: "/xero" },
  zid: { store: STORES.ZID, path: "/zid" },
  zohobooks: { store: STORES.ZOHOBOOKS, path: "/zohobooks" },
};

export interface Platform {
  name: string;
  platformEnum: string;
}

export interface PlatformDetails {
  store: STORES;
  path: string;
}

export const getStoreFromPlatform = (platform: Platform): STORES => {
  const defaultName = platform.platformEnum ?? platform.name;
  return STORES[defaultName.toUpperCase() as keyof typeof STORES];
};

export const getPlatformDetailsFromName = (
  stateStore: Store<StoreType>,
  platform: Platform,
): PlatformDetails => {
  const platformName = platform.name.toLowerCase();
  let platformDetails = PLATFORM_DETAILS_BY_NAME[platformName];

  if (!platformDetails) {
    // Generic-auth Routes are generated at `/${platformEnum.toLowerCase()}`
    // (see generateRouteBlock in Link.tsx), so prefer `platformEnum` over
    // `name`. `name` can contain spaces / slashes (e.g. "SAP S/4HANA") that
    // produce invalid paths and send the user back to the splash screen.
    const fallbackPath = platform.platformEnum
      ? `/${platform.platformEnum.toLowerCase()}`
      : `/${platform.name.toLowerCase()}`;
    platformDetails = {
      // This will not error if the value is not represented in STORES.
      store: getStoreFromPlatform(platform),
      // 8/24/2022 Add query parameters to allow refreshing/goback of the page and everything still works, for example chargify
      path: fallbackPath,
    };
  }

  // Store store and platform so they can be used to track metrics after completion.
  stateStore.set("store")(platformDetails.store);
  stateStore.set("platform")(platform);
  return platformDetails;
};

export enum METRIC_EVENTS {
  CREATE_CONNECTION_START = "CREATE_CONNECTION_START",
  CREATE_CONNECTION_SUCCESS = "CREATE_CONNECTION_SUCCESS",
}

export enum AMPLITUDE_EVENTS {
  UPDATE_METRIC_ATTEMPT = "UPDATE_METRIC_ATTEMPT",
  UPDATE_METRIC_SUCCESS = "UPDATE_METRIC_SUCCESS",
  UPDATE_METRIC_ERROR = "UPDATE_METRIC_ERROR",
}

interface UpdateMetricProps {
  itemId: string;
  orgId: string;
  store: STORES | null;
  platform: Platform | null;
}

export const sendConnectionStartMetric = (props: UpdateMetricProps) => {
  localStorage.setItem("updateMetricProps", JSON.stringify(props));
  updateMetrics(METRIC_EVENTS.CREATE_CONNECTION_START, props);
};

export const sendConnectionSuccessMetric = () => {
  const props = JSON.parse(
    localStorage.getItem("updateMetricProps") ?? "{}",
  ) as UpdateMetricProps;
  updateMetrics(METRIC_EVENTS.CREATE_CONNECTION_SUCCESS, props);
  localStorage.removeItem("updateMetricProps");
};

export const updateMetrics = (
  event: METRIC_EVENTS,
  props: {
    itemId: string;
    orgId: string;
    store: STORES | null;
    platform: Platform | null;
  },
) => {
  const { itemId, orgId, store, platform } = props;
  amplitude.track(AMPLITUDE_EVENTS.UPDATE_METRIC_ATTEMPT, {
    event,
    itemId,
    orgId,
    store,
    platform,
  });

  axios
    .post(`${getBackendUrl()}/link/updateMetrics`, {
      itemId,
      orgId,
      platform: store ?? platform?.platformEnum,
      event,
    })
    .then((_response) => {
      amplitude.track(AMPLITUDE_EVENTS.UPDATE_METRIC_SUCCESS, {
        event,
        itemId,
        orgId,
        store,
        platform,
      });
    })
    .catch((err) => {
      // Fail silently since we don't want to disrupt the Rutter link flow,
      // but log the failure in Amplitude.
      const errorMessage = err.response?.data?.error_message ?? err.message;
      amplitude.track(AMPLITUDE_EVENTS.UPDATE_METRIC_ERROR, {
        event,
        itemId,
        orgId,
        store,
        platform,
        errorMessage,
      });
    });
};

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
